import { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Skeleton,
    Chip,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    Button,
} from "@mui/material";
import { getEarnings } from "../../../api/stocks_api";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ArticleIcon from "@mui/icons-material/Article";
import DescriptionIcon from "@mui/icons-material/Description";
import GavelIcon from "@mui/icons-material/Gavel";
import { getCurrencySymbol, fmtL } from "../../../utils/currency"; // ← adjust path


const Earnings = ({ stock }) => {
    const [earnings, setEarnings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);
    const [lastFetched, setLastFetched] = useState(null);
    const sym = getCurrencySymbol();

    const fetchEarnings = async () => {
        if (!stock?.ticker) return;
        setLoading(true);
        setFetchError(false);
        const res = await getEarnings(stock.ticker);
        if (res.success) {
            setEarnings(res.response_data || []);
            setLastFetched(new Date());
        } else {
            setFetchError(true);
            /* Keep stale data if we have it (6.1.5) */
            if (earnings.length === 0) setEarnings([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchEarnings();
    }, [stock?.ticker]);

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return "—";
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    };

    const formatRevenue = (value) => {
        if (value === null || value === undefined) return "—";
        const billions = value / 1000000000;
        return `${sym}${billions.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}B`;
    };

    const formatPercentage = (value) => {
        if (value === null || value === undefined) return "—";
        const sign = value >= 0 ? "+" : "";
        return `${sign}${fmtL(value, 2)}%`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const formatEPS = (value) =>
        value === null ? "—" : `${sym}${fmtL(value, 2)}`;


    const SurpriseChip = ({ value }) => {
        if (value === null) {
            return (
                <Chip
                    label="Pending"
                    size="small"
                    sx={{
                        bgcolor: "var(--color-surface-hover)",
                        color: "var(--color-text-muted)",
                        fontSize: "var(--font-size-sm)",
                        fontWeight: 600,
                        height: 24,
                    }}
                />
            );
        }

        const isBeat = value === true;

        return (
            <Chip
                icon={
                    isBeat ? (
                        <TrendingUpIcon sx={{ fontSize: 14 }} />
                    ) : (
                        <TrendingDownIcon sx={{ fontSize: 14 }} />
                    )
                }
                label={isBeat ? "Beat" : "Miss"}
                size="small"
                sx={{
                    bgcolor: isBeat ? "#065f46" : "#7f1d1d",
                    color: isBeat ? "#34d399" : "#fca5a5",
                    fontSize: "var(--font-size-sm)",
                    fontWeight: 600,
                    height: 24,
                    "& .MuiChip-icon": {
                        color: isBeat ? "#34d399" : "#fca5a5",
                    },
                }}
            />
        );
    };


    if (loading) {
        return (
            <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
                <Skeleton
                    variant="rectangular"
                    height={400}
                    sx={{
                        borderRadius: "var(--radius-xl)",
                        bgcolor: "var(--color-surface-hover)",
                        "&::after": {
                            background:
                                "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)",
                        },
                    }}
                />
            </Box>
        );
    }

    if (earnings.length === 0) {
        return (
            <Box
                sx={{
                    px: { xs: 2, md: 3 },
                    py: 2,
                }}
            >
                <Box
                    sx={{
                        p: 6,
                        bgcolor: "var(--color-surface)",
                        border: "1px solid var(--color-border-primary)",
                        borderRadius: "var(--radius-xl)",
                        textAlign: "center",
                    }}
                >
                    <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" fontWeight={500}>
                        No earnings data available for this stock
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
            <Box
                sx={{
                    bgcolor: "var(--color-surface)",
                    border: "1px solid var(--color-border-primary)",
                    borderRadius: "var(--radius-xl)",
                    overflow: "hidden",
                }}
            >
                {/* Stale data / error banner (6.1.5) */}
                {fetchError && earnings.length > 0 && (
                    <Alert
                        severity="warning"
                        action={
                            <Button color="inherit" size="small" onClick={fetchEarnings} sx={{ textTransform: "none", fontWeight: 600 }}>
                                Retry
                            </Button>
                        }
                        sx={{
                            bgcolor: "var(--color-surface-hover)",
                            color: "var(--color-text-primary)",
                            borderBottom: "1px solid var(--color-border-primary)",
                            borderRadius: 0,
                            "& .MuiAlert-icon": { color: "var(--color-warning, #f59e0b)" },
                        }}
                    >
                        Unable to refresh data. Showing previously loaded results
                        {lastFetched ? ` from ${lastFetched.toLocaleTimeString()}` : ""}.
                    </Alert>
                )}

                {/* Header */}
                <Box sx={{ p: 3, borderBottom: "1px solid var(--color-border-primary)" }}>
                    <Typography fontSize="var(--font-size-lg)" fontWeight={700} color="var(--color-text-primary)">
                        Earnings History
                    </Typography>
                    <Typography fontSize="var(--font-size-base)" color="var(--color-text-muted)" mt={0.5}>
                        Historical earnings reports and estimates
                    </Typography>
                </Box>

                {/* Table */}
                <TableContainer component={Paper} sx={{ bgcolor: "transparent", boxShadow: "none" }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: "var(--color-bg-secondary)" }}>
                                <TableCell
                                    sx={{
                                        color: "var(--color-text-muted)",
                                        fontWeight: 600,
                                        fontSize: "var(--font-size-sm)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        borderBottom: "1px solid var(--color-border-primary)",
                                        py: 2,
                                    }}
                                >
                                    Date
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: "var(--color-text-muted)",
                                        fontWeight: 600,
                                        fontSize: "var(--font-size-sm)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        borderBottom: "1px solid var(--color-border-primary)",
                                        py: 2,
                                    }}
                                >
                                    Quarter
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: "var(--color-text-muted)",
                                        fontWeight: 600,
                                        fontSize: "var(--font-size-sm)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        borderBottom: "1px solid var(--color-border-primary)",
                                        py: 2,
                                    }}
                                >
                                    EPS
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: "var(--color-text-muted)",
                                        fontWeight: 600,
                                        fontSize: "var(--font-size-sm)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        borderBottom: "1px solid var(--color-border-primary)",
                                        py: 2,
                                    }}
                                >
                                    Revenue
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: "var(--color-text-muted)",
                                        fontWeight: 600,
                                        fontSize: "var(--font-size-sm)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        borderBottom: "1px solid var(--color-border-primary)",
                                        py: 2,
                                    }}
                                >
                                    Result
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: "var(--color-text-muted)",
                                        fontWeight: 600,
                                        fontSize: "var(--font-size-sm)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        borderBottom: "1px solid var(--color-border-primary)",
                                        py: 2,
                                    }}
                                >
                                    Documents
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {earnings.map((earning, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        "&:hover": {
                                            "& td": {
                                                backgroundColor: "var(--color-surface-hover)",
                                            },
                                        },
                                        transition: "var(--transition-fast)",
                                    }}
                                >
                                    {/* Date */}
                                    <TableCell
                                        sx={{
                                            borderBottom: "1px solid var(--color-border-primary)",
                                            py: 2.5,
                                        }}
                                    >
                                        <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" fontWeight={500}>
                                            {formatDate(earning.report_date)}
                                        </Typography>
                                        {earning.report_time && earning.report_time !== "Unknown" && (
                                            <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-muted)" mt={0.3}>
                                                {earning.report_time}
                                            </Typography>
                                        )}
                                    </TableCell>

                                    {/* Quarter */}
                                    <TableCell
                                        sx={{
                                            borderBottom: "1px solid var(--color-border-primary)",
                                            py: 2.5,
                                        }}
                                    >
                                        <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" fontWeight={500}>
                                            Q{earning.fiscal_quarter} {earning.fiscal_year}
                                        </Typography>
                                    </TableCell>

                                    {/* EPS */}
                                    <TableCell
                                        sx={{
                                            borderBottom: "1px solid var(--color-border-primary)",
                                            py: 2.5,
                                        }}
                                    >
                                        <Box>
                                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
                                                    Est:
                                                </Typography>
                                                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" fontWeight={500}>
                                                    {formatEPS(earning.eps_estimate)}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
                                                    Act:
                                                </Typography>
                                                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" fontWeight={600}>
                                                    {earning.eps_actual !== null
                                                        ? formatEPS(earning.eps_actual)
                                                        : (
                                                            <Chip
                                                                label="Upcoming"
                                                                size="small"
                                                                sx={{
                                                                    mt: 0.5,
                                                                    bgcolor: "var(--color-surface-hover)",
                                                                    color: "var(--color-primary)",
                                                                    fontSize: "var(--font-size-sm)",
                                                                    height: 20,
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                        )}

                                                </Typography>
                                            </Box>
                                            {earning.eps_surprise !== null && (
                                                <Typography
                                                    fontSize="var(--font-size-sm)"
                                                    color={earning.eps_surprise >= 0 ? "#34d399" : "#fca5a5"}
                                                    mt={0.5}
                                                    fontWeight={600}
                                                >
                                                    {earning.eps_surprise >= 0 ? "+" : "-"}
                                                    {sym}{fmtL(Math.abs(earning.eps_surprise), 2)}
                                                    {" "}
                                                    ({formatPercentage(earning.eps_surprise_pct)})
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>

                                    {/* Revenue */}
                                    <TableCell
                                        sx={{
                                            borderBottom: "1px solid var(--color-border-primary)",
                                            py: 2.5,
                                        }}
                                    >
                                        <Box>
                                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
                                                    Est:
                                                </Typography>
                                                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" fontWeight={500}>
                                                    {formatRevenue(earning.revenue_estimate)}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
                                                    Act:
                                                </Typography>
                                                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" fontWeight={600}>
                                                    {earning.revenue_actual !== null
                                                        ? formatRevenue(earning.revenue_actual)
                                                        : "—"}
                                                </Typography>
                                            </Box>
                                            {earning.revenue_surprise !== null && (
                                                <Typography
                                                    fontSize="var(--font-size-sm)"
                                                    color={earning.revenue_surprise >= 0 ? "#34d399" : "#fca5a5"}
                                                    mt={0.5}
                                                    fontWeight={600}
                                                >
                                                    {earning.revenue_surprise >= 0 ? "+" : "-"}
                                                    {sym}{fmtL(Math.abs(earning.revenue_surprise) / 1e9, 2)}B
                                                    {" "}
                                                    ({formatPercentage(earning.revenue_surprise_pct)})
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>

                                    {/* Result */}
                                    <TableCell
                                        sx={{
                                            borderBottom: "1px solid var(--color-border-primary)",
                                            py: 2.5,
                                        }}
                                    >
                                        {/* <SurpriseChip
                      value={earning.beat_estimate}
                      label={
                        earning.beat_estimate === null
                          ? "Pending"
                          : earning.beat_estimate
                          ? "Beat"
                          : "Miss"
                      }
                    /> */}
                                        <SurpriseChip value={earning.beat_estimate} />

                                    </TableCell>

                                    {/* Documents */}
                                    <TableCell
                                        sx={{
                                            borderBottom: "1px solid var(--color-border-primary)",
                                            py: 2.5,
                                        }}
                                    >
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                            {earning.transcript_url && (
                                                <Link
                                                    href={earning.transcript_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        px: 1.5,
                                                        py: 0.5,
                                                        bgcolor: "var(--color-surface-hover)",
                                                        border: "1px solid var(--color-border-primary)",
                                                        borderRadius: "var(--radius-md)",
                                                        textDecoration: "none",
                                                        fontSize: "var(--font-size-sm)",
                                                        color: "var(--color-primary)",
                                                        fontWeight: 500,
                                                        "&:hover": {
                                                            bgcolor: "var(--color-bg-secondary)",
                                                            borderColor: "var(--color-border-secondary)",
                                                        },
                                                        transition: "var(--transition-fast)",
                                                    }}
                                                >
                                                    <ArticleIcon sx={{ fontSize: 14 }} />
                                                    Transcript
                                                </Link>
                                            )}
                                            {earning.press_release_url && (
                                                <Link
                                                    href={earning.press_release_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        px: 1.5,
                                                        py: 0.5,
                                                        bgcolor: "var(--color-surface-hover)",
                                                        border: "1px solid var(--color-border-primary)",
                                                        borderRadius: "var(--radius-md)",
                                                        textDecoration: "none",
                                                        fontSize: "var(--font-size-sm)",
                                                        color: "var(--color-primary)",
                                                        fontWeight: 500,
                                                        "&:hover": {
                                                            bgcolor: "var(--color-bg-secondary)",
                                                            borderColor: "var(--color-border-secondary)",
                                                        },
                                                        transition: "var(--transition-fast)",
                                                    }}
                                                >
                                                    <DescriptionIcon sx={{ fontSize: 14 }} />
                                                    Press Release
                                                </Link>
                                            )}
                                            {earning.sec_filing_url && (
                                                <Link
                                                    href={earning.sec_filing_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        px: 1.5,
                                                        py: 0.5,
                                                        bgcolor: "var(--color-surface-hover)",
                                                        border: "1px solid var(--color-border-primary)",
                                                        borderRadius: "var(--radius-md)",
                                                        textDecoration: "none",
                                                        fontSize: "var(--font-size-sm)",
                                                        color: "var(--color-primary)",
                                                        fontWeight: 500,
                                                        "&:hover": {
                                                            bgcolor: "var(--color-bg-secondary)",
                                                            borderColor: "var(--color-border-secondary)",
                                                        },
                                                        transition: "var(--transition-fast)",
                                                    }}
                                                >
                                                    <GavelIcon sx={{ fontSize: 14 }} />
                                                    SEC Filing
                                                </Link>
                                            )}
                                            {!earning.transcript_url &&
                                                !earning.press_release_url &&
                                                !earning.sec_filing_url && (
                                                    <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-muted)">
                                                        —
                                                    </Typography>
                                                )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default Earnings;